import {
  Box,
  FormControlLabel,
  Grid,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from "@mui/material";
import { useForm, Controller } from "react-hook-form";
import ReadMoreReadLess from "../../app/common/ReadMoreReadLess";
import { AnswersProps } from "../../app/models/Answers";
import { useEffect, useState } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useAppSelector } from "../../app/store/configureStore";

const schema = yup.object().shape({
  menuCommitment: yup
    .string()
    .required("Must give an answer")
    .max(500, "Max characters reached"),
  strengthsMenu: yup
    .string()
    .required("Must give an answer")
    .max(500, "Max characters reached"),
  improveMenu: yup
    .string()
    .required("Must give an answer")
    .max(500, "Max characters reached"),
});

const StatementMenuReflection: React.FC<AnswersProps> = ({
  answersData,
  onAnswerChange,
}) => {
  const { user } = useAppSelector((state) => state.account);
  const isInfantToddler = user?.credentialTypeId === 1;

  const [selectedOption, setSelectedOption] = useState<boolean | null>(null);

  const optionSystemCode = isInfantToddler ? 26 : 3600;
  const menuCommitmentSystemCode = isInfantToddler ? 27 : 3601;
  const strengthMenuSystemCode = isInfantToddler ? 28 : 3602;
  const improvMenuSystemCode = isInfantToddler ? 29 : 3603;

  const defaultValues = {
    optionRadio: answersData.find(
      (answer) => answer.systemCodeId === optionSystemCode
    )?.answer,
    menuCommitment: answersData.find(
      (answer) => answer.systemCodeId === menuCommitmentSystemCode
    )?.answer,
    strengthsMenu: answersData.find(
      (answer) => answer.systemCodeId === strengthMenuSystemCode
    )?.answer,
    improveMenu: answersData.find(
      (answer) => answer.systemCodeId === improvMenuSystemCode
    )?.answer,
  };

  const {
    control,
    watch,
    register,
    formState: { errors },
  } = useForm({
    defaultValues,
    resolver: yupResolver(schema),
    mode: "onTouched",
  });

  const Option = watch("optionRadio");

  useEffect(() => {
    if (Option) {
      setSelectedOption(Option === "true");
    }
  }, [Option, selectedOption]);

  const seeMenuReflection = (
    <>
      <li>How do you ensure children are offered a variety of foods daily?</li>
      <li>How are allergens and food sensitivities managed?</li>
      <li>How are meal patterns considered when planning menus?</li>
      <li>
        What steps are used to ensure the nutritional guidelines and
        requirements are followed and implemented for every meal?
      </li>
    </>
  );

  const seeMenuStrengths = (
    <>
      <li>
        How do you ensure children are offered a variety of foods each day?
      </li>
      <li>
        What are the alternatives if a child has a food allergy or sensitivity?
      </li>
      <li>
        How do the menu selections reflect nutritional guidelines and
        requirements?
      </li>
      <li>How is the menu or feeding schedule age-appropriate?</li>
    </>
  );

  const seeMenuImprovements = (
    <>
      <li>How would you make sure variety of foods are offered each day?</li>
      <li>
        What steps could you take to ensure alternatives are incorporated for
        children with allergies or food sensitivities?
      </li>
      <li>How do you ensure nutritional guidelines are met for every meal?</li>
    </>
  );
  return (
    <Grid item xs={12} px={3} py={3} sx={{ bgcolor: "#EBFCF2" }}>
      <Typography variant="body1" fontWeight="bold" paragraph>
        CS I-a - Menu Reflection
      </Typography>
      {isInfantToddler ? (
        <Typography variant="body1" paragraph>
          Review and think about the sample menu(s) and/or feeding schedule you
          provided for your Resource Collection (RC I-2), and then respond to
          the following questions:
        </Typography>
      ) : (
        <Typography variant="body1" paragraph>
          Review and think about the sample menu(s) you provided for your
          Resource Collection (RC I-2), and then respond to the following
          questions:
        </Typography>
      )}
      <Box component="form">
        {isInfantToddler ? (
          <Typography variant="body1">
            Did you design the menu or feeding schedule?
          </Typography>
        ) : (
          <Typography variant="body1">Did you design the menu?</Typography>
        )}
        <Controller
          name="optionRadio"
          control={control}
          render={({ field }) => (
            <RadioGroup
              name="optionRadio"
              value={field.value}
              onChange={(e) => {
                field.onChange(e);
                const newValue = e.target.value;
                const systemCodeId = optionSystemCode;

                onAnswerChange(systemCodeId, newValue);
              }}
            >
              <FormControlLabel
                value={true}
                control={<Radio />}
                label="Yes"
                labelPlacement="end"
              />
              <FormControlLabel
                value={false}
                control={<Radio />}
                label="No"
                labelPlacement="end"
              />
            </RadioGroup>
          )}
        />

        {selectedOption === true && (
          <Box>
            <Grid item xs={12} pt={3}>
              {isInfantToddler ? (
                <Typography variant="body1" paragraph>
                  How does this menu or feeding schedule show your commitment to
                  meeting children's nutritional needs?
                </Typography>
              ) : (
                <Typography variant="body1" paragraph>
                  How does this menu show your commitment to meeting children's
                  nutritional needs?
                </Typography>
              )}

              <ReadMoreReadLess>{seeMenuReflection}</ReadMoreReadLess>
            </Grid>
            <Grid item xs={12} py={3}>
              <TextField
                label="I designed the menu in RC I-2, and it shows my commitment to meeting nutritional needs because"
                {...register("menuCommitment")}
                sx={{ bgcolor: "#FFF" }}
                value={
                  answersData.find(
                    (answer) => answer.systemCodeId === menuCommitmentSystemCode
                  )?.answer || ""
                }
                onChange={(e) => {
                  const newValue = e.target.value;
                  const systemCodeId = menuCommitmentSystemCode;
                  newValue.length <= 500 &&
                    onAnswerChange(systemCodeId, newValue);
                }}
                multiline
                rows={2}
                fullWidth
                error={!!errors.menuCommitment?.message}
                helperText={errors.menuCommitment?.message}
              />
              <Typography fontSize={13}>
                {defaultValues.menuCommitment?.length}/500
              </Typography>
            </Grid>
          </Box>
        )}
        {selectedOption === false && (
          <Box>
            <Grid item xs={12} pt={3}>
              <Typography variant="body1" paragraph>
                {isInfantToddler
                  ? `What are the strengths of this menu or feeding schedule?`
                  : `What are the strengths of this menu?`}
              </Typography>
              <ReadMoreReadLess>{seeMenuStrengths}</ReadMoreReadLess>
            </Grid>
            <Grid item xs={12} py={3}>
              <TextField
                label={
                  isInfantToddler
                    ? "While I did not design the menu (or feeding schedule) in RC I-2, its strengths are "
                    : "While I did not design the menu in RC I-2, its strengths are"
                }
                {...register("strengthsMenu")}
                sx={{ bgcolor: "#FFF" }}
                value={
                  answersData.find(
                    (answer) => answer.systemCodeId === strengthMenuSystemCode
                  )?.answer || ""
                }
                onChange={(e) => {
                  const newValue = e.target.value;
                  const systemCodeId = strengthMenuSystemCode;

                  newValue.length <= 500 &&
                    onAnswerChange(systemCodeId, newValue);
                }}
                multiline
                rows={2}
                fullWidth
                error={!!errors.strengthsMenu?.message}
                helperText={errors.strengthsMenu?.message}
              />
              <Typography fontSize={13}>
                {defaultValues.strengthsMenu?.length}/500
              </Typography>
            </Grid>
            <Grid item xs={12} pt={3}>
              <Typography variant="body1" paragraph>
                {isInfantToddler
                  ? `How would you improve this menu or feeding schedule?`
                  : `How would you improve this menu?`}
              </Typography>
              {<ReadMoreReadLess>{seeMenuImprovements}</ReadMoreReadLess>}
            </Grid>
            <Grid item xs={12} py={3}>
              <TextField
                label="I would improve this menu by "
                {...register("improveMenu")}
                name="improveMenu"
                sx={{ bgcolor: "#FFF" }}
                value={
                  answersData.find(
                    (answer) => answer.systemCodeId === improvMenuSystemCode
                  )?.answer || ""
                }
                onChange={(e) => {
                  const newValue = e.target.value;
                  const systemCodeId = improvMenuSystemCode;

                  newValue.length <= 500 &&
                    onAnswerChange(systemCodeId, newValue);
                }}
                multiline
                rows={2}
                fullWidth
                error={!!errors.improveMenu?.message}
                helperText={errors.improveMenu?.message}
              />
              <Typography fontSize={13}>
                {defaultValues.improveMenu?.length}/500
              </Typography>
            </Grid>
          </Box>
        )}
      </Box>
    </Grid>
  );
};
export default StatementMenuReflection;
